/**
 * Shared design style-code lookup (design style-code plan, Phase 3). Turns a placement's `designId`
 * into its short SKU-style code (FR / FW / LC / LT-001) for the Rollo label + the scan-station tiles —
 * a LIVE join at render, so the code is never denormalized through the frozen batch chain and always
 * reflects the current design record.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchAllDesigns } from '@/lib/api/designs';
import { useApiClient } from '@/lib/api/hooks';

/**
 * `designId → styleCode` for the designs that have a code. Shares the `['designs']` cache with the
 * Design library page, so it's usually already warm; a miss (design without a code, or a load failure)
 * just omits the entry, and the label/tile falls back to the design name — so it degrades gracefully.
 */
export function useStyleCodes(): Map<string, string> {
  return useStyleCodesQuery().styleCodeByDesignId;
}

/**
 * The same join plus its LOAD STATE. See {@link useBlankNamesQuery} for the reasoning — a cold map here
 * degrades every badge to the legacy first-two-letters form, which on a REPRINT means the reissued tag
 * reads differently from the original.
 */
export function useStyleCodesQuery(): {
  styleCodeByDesignId: Map<string, string>;
  isResolved: boolean;
  isError: boolean;
} {
  const client = useApiClient();
  const { data, isSuccess, isError } = useQuery({
    queryKey: ['designs'],
    queryFn: () => fetchAllDesigns(client),
    staleTime: 5 * 60_000,
  });
  const styleCodeByDesignId = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of data ?? []) if (d.styleCode) m.set(d.designId, d.styleCode);
    return m;
  }, [data]);
  return { styleCodeByDesignId, isResolved: isSuccess, isError };
}
